import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { isNativePlatform } from '@/lib/native'

/**
 * Open a stored PDF (an /api/images/:key reference) for the customer.
 *
 * Two very different environments run this same bundle:
 *
 * - LINE's in-app browser, where `window.open` is what works and has already
 *   been tuned to stay synchronous so LINE does not treat it as a popup.
 * - The Capacitor customer app, where programmatic `window.open` is handled by
 *   WKUIDelegate.createWebViewWith — which neither iOS shell implements, so the
 *   call silently does nothing. That is why "view lease" did nothing in the app.
 *
 * In the native shell the bytes are therefore written to the cache directory
 * and handed to the OS share sheet, which offers Open in / Save to Files /
 * print. That keeps the customer inside the app rather than bouncing them to
 * Safari, and gives them a file they can keep.
 */

/** Resolve /api/images/:key to a directly fetchable URL (see web/lib/imageUrl). */
async function resolveUrl(key: string): Promise<string> {
  const base = import.meta.env.VITE_API_URL || ''
  // cache: 'no-store' is belt-and-braces against the content-negotiation bug
  // this hit in production: the endpoint answers the SAME url with a 302 for a
  // browser and JSON for XHR, and its redirect variant shipped without
  // `Vary: Accept`. HomePage's mount probe (no Accept) filled the WKWebView
  // cache with the 302, and this request then revalidated into a 304 with an
  // empty body. The server now varies correctly; this keeps an old cache entry
  // (or any intermediary that ignores Vary) from resurrecting the failure.
  const res = await fetch(`${base}/api/images/${key}`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  })
  if (!res.ok) throw new Error(`Could not resolve ${key} (${res.status})`)
  const { url } = (await res.json()) as { url: string }
  return url
}

/** Chunked so a multi-MB lease does not blow the argument limit of String.fromCharCode. */
function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

// Every failure is tagged with the stage that produced it. A bare message here
// is close to useless: the whole class of bug this function exists to fix is
// invisible-at-runtime, so when it breaks again the operator needs to know
// whether the resolve, the download, the write or the share sheet was at fault.
export type PdfStage = 'platform' | 'resolve' | 'download' | 'write' | 'share'

export class OpenPdfError extends Error {
  // Explicit field, not a constructor parameter property: the project builds
  // with erasableSyntaxOnly, which bans the shorthand.
  readonly stage: PdfStage

  constructor(stage: PdfStage, message: string) {
    super(`[${stage}] ${message}`)
    this.name = 'OpenPdfError'
    this.stage = stage
  }
}

// The plugin reports a dismissed sheet through the error channel, with no code
// to branch on — only a message. Matched loosely (both spellings, any case)
// because the wording is the plugin's, not ours, and a copy change upstream
// must not resurrect a spurious error toast.
function isShareDismissal(e: unknown): boolean {
  const message = (e instanceof Error ? e.message : String(e)).toLowerCase()
  return message.includes('cancel') || message.includes('abort')
}

export async function openStoredPdf(key: string, filename: string): Promise<PdfStage> {
  const base = import.meta.env.VITE_API_URL || ''

  if (!isNativePlatform()) {
    // Kept synchronous: LINE's in-app browser blocks a window.open that happens
    // after an await.
    window.open(`${base}/api/images/${key}`, '_blank')
    return 'platform'
  }

  let url: string
  try {
    url = await resolveUrl(key)
  } catch (e) {
    throw new OpenPdfError('resolve', e instanceof Error ? e.message : String(e))
  }

  let data: string
  try {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    data = toBase64(await res.arrayBuffer())
  } catch (e) {
    throw new OpenPdfError('download', e instanceof Error ? e.message : String(e))
  }

  let uri: string
  try {
    // Cache, not Documents: this is a convenience copy the OS may reclaim, and
    // it does not need to appear in the app's own file listing.
    const written = await Filesystem.writeFile({ path: filename, data, directory: Directory.Cache })
    uri = written.uri
  } catch (e) {
    throw new OpenPdfError('write', e instanceof Error ? e.message : String(e))
  }

  try {
    await Share.share({ title: filename, url: uri })
  } catch (e) {
    // Dismissing the sheet is a REJECTION, not a failure: the iOS plugin
    // rejects with "Share canceled" when the user taps outside it. Treating
    // that as an error showed "something went wrong" to a tenant who simply
    // finished looking at their lease (owner report 2026-08-20). The user
    // chose to stop, so the operation succeeded as far as they are concerned.
    if (isShareDismissal(e)) return 'share'

    throw new OpenPdfError('share', e instanceof Error ? e.message : String(e))
  }

  return 'share'
}
